import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

const emptyForm = {
  email: '',
  name: '',
  phone: '',
  source: 'admin',
  tags: '',
  notes: '',
  status: 'subscribed',
  unsubscribeReason: ''
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

const normalizeTagInput = (value) =>
  String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const statusChipColor = (status) => {
  if (status === 'subscribed') return 'success';
  if (status === 'unsubscribed') return 'default';
  return 'default';
};

function SubscriberFormDialog({
  open,
  mode,
  formData,
  submitting,
  onClose,
  onChange,
  onSubmit
}) {
  const isEdit = mode === 'edit';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'Edit Subscriber' : 'Add Subscriber'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              name="email"
              value={formData.email}
              onChange={onChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              name="name"
              value={formData.name}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Source"
              name="source"
              value={formData.source}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              name="status"
              value={formData.status}
              onChange={onChange}
            >
              <MenuItem value="subscribed">Subscribed</MenuItem>
              <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Tags"
              name="tags"
              value={formData.tags}
              onChange={onChange}
              helperText="Comma separated"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={onChange}
              multiline
              minRows={3}
            />
          </Grid>
          {formData.status === 'unsubscribed' ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Unsubscribe Reason"
                name="unsubscribeReason"
                value={formData.unsubscribeReason}
                onChange={onChange}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Update Subscriber' : 'Create Subscriber'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscriberDetailsDialog({ open, data, loading, onClose }) {
  const subscriber = data?.subscriber || null;
  const sourceBreakdown = [
    ['Email', subscriber?.email || '-'],
    ['Name', subscriber?.name || '-'],
    ['Phone', subscriber?.phone || '-'],
    ['Status', subscriber?.status || '-'],
    ['Source', subscriber?.source || '-'],
    ['Subscribed At', formatDate(subscriber?.subscribedAt)],
    ['Unsubscribed At', formatDate(subscriber?.unsubscribedAt)],
    ['Created At', formatDate(subscriber?.createdAt)],
    ['Updated At', formatDate(subscriber?.updatedAt)],
    ['User', subscriber?.user?.email || subscriber?.user?.name || '-'],
    ['Reason', subscriber?.unsubscribeReason || '-']
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Subscriber Details</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography>Loading subscriber...</Typography>
        ) : !subscriber ? (
          <Typography>No subscriber details available.</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" color={statusChipColor(subscriber.status)} label={subscriber.status || 'unknown'} />
              {(subscriber.tags || []).map((tag) => (
                <Chip key={tag} size="small" variant="outlined" label={tag} />
              ))}
            </Stack>
            <Grid container spacing={2}>
              {sourceBreakdown.map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Consent
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Explicit opt-in: {subscriber?.consent?.explicitOptIn ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  Marketing: {subscriber?.consent?.marketing ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Meta
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  IP: {subscriber?.meta?.ipAddress || '-'}
                </Typography>
                <Typography variant="body2">Locale: {subscriber?.meta?.locale || '-'}</Typography>
                <Typography variant="body2">Referrer: {subscriber?.meta?.referrer || '-'}</Typography>
              </Grid>
            </Grid>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {subscriber?.notes || '-'}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function NewsletterSubscribers() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  const listEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    return `/api/newsletter/admin/subscribers?${params.toString()}`;
  }, [page, rowsPerPage, search, sourceFilter, statusFilter]);

  const detailsEndpoint = detailsOpen && selectedSubscriberId
    ? `/api/newsletter/admin/subscribers/${selectedSubscriberId}`
    : null;

  const { data, loading, error, mutate } = useGet(listEndpoint);
  const { data: statsData, error: statsError, mutate: mutateStats } = useGet('/api/newsletter/admin/stats');
  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError
  } = useGet(detailsEndpoint);

  const subscribers = data?.subscribers || [];
  const pagination = data?.pagination || {};
  const stats = statsData?.stats || {};

  const sourceOptions = useMemo(() => {
    const set = new Set();
    (stats?.sourceBreakdown || []).forEach((item) => {
      if (item?.source) set.add(item.source);
    });
    subscribers.forEach((subscriber) => {
      if (subscriber?.source) set.add(subscriber.source);
    });
    return Array.from(set).sort((left, right) => left.localeCompare(right));
  }, [stats?.sourceBreakdown, subscribers]);

  const resetMessages = () => {
    setActionError('');
    setActionSuccess('');
  };

  const refreshAll = async () => {
    resetMessages();
    setRefreshing(true);
    try {
      await Promise.all([mutate(), mutateStats()]);
    } finally {
      setRefreshing(false);
    }
  };

  const openCreateDialog = () => {
    resetMessages();
    setFormMode('create');
    setFormData(emptyForm);
    setFormOpen(true);
  };

  const openEditDialog = (subscriber) => {
    resetMessages();
    setFormMode('edit');
    setSelectedSubscriberId(subscriber?._id || '');
    setFormData({
      email: subscriber?.email || '',
      name: subscriber?.name || '',
      phone: subscriber?.phone || '',
      source: subscriber?.source || 'admin',
      tags: Array.isArray(subscriber?.tags) ? subscriber.tags.join(', ') : '',
      notes: subscriber?.notes || '',
      status: subscriber?.status || 'subscribed',
      unsubscribeReason: subscriber?.unsubscribeReason || ''
    });
    setFormOpen(true);
  };

  const openDetailsDialog = (subscriberId) => {
    resetMessages();
    setSelectedSubscriberId(subscriberId);
    setDetailsOpen(true);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    resetMessages();
    setSubmitting(true);

    const payload = {
      email: formData.email,
      name: formData.name,
      phone: formData.phone,
      source: formData.source,
      tags: normalizeTagInput(formData.tags)
    };

    if (formMode === 'edit') {
      payload.notes = formData.notes;
      payload.status = formData.status;
      payload.unsubscribeReason = formData.status === 'unsubscribed' ? formData.unsubscribeReason : '';
    }

    try {
      if (formMode === 'create') {
        await apiClient.post('/api/newsletter/admin/subscribers', payload);
        setActionSuccess('Subscriber created successfully.');
      } else {
        await apiClient.patch(`/api/newsletter/admin/subscribers/${selectedSubscriberId}`, payload);
        setActionSuccess('Subscriber updated successfully.');
      }

      setFormOpen(false);
      await Promise.all([mutate(), mutateStats()]);
    } catch (requestError) {
      setActionError(requestError?.response?.data?.message || 'Failed to save subscriber.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (subscriber) => {
    const subscriberId = subscriber?._id;
    if (!subscriberId) return;

    const confirmed = window.confirm(`Delete subscriber ${subscriber.email || subscriberId}?`);
    if (!confirmed) return;

    resetMessages();
    setSubmitting(true);
    try {
      await apiClient.delete(`/api/newsletter/admin/subscribers/${subscriberId}`);
      setActionSuccess('Subscriber deleted successfully.');
      if (selectedSubscriberId === subscriberId) {
        setSelectedSubscriberId('');
        setDetailsOpen(false);
      }
      await Promise.all([mutate(), mutateStats()]);
    } catch (requestError) {
      setActionError(requestError?.response?.data?.message || 'Failed to delete subscriber.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const response = await apiClient.get(`/api/newsletter/admin/subscribers/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'newsletter-subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setActionError(requestError?.response?.data?.message || 'Failed to export subscribers.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
      >
        <Box>
          <Typography variant="h4">Newsletter Subscribers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage newsletter subscribers, status changes, exports, and source tracking.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={submitting}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={refreshAll}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Subscriber
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <MainCard>
            <Typography variant="body2" color="text.secondary">
              Total Subscribers
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              {stats.totalSubscribers || 0}
            </Typography>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MainCard>
            <Typography variant="body2" color="text.secondary">
              Active Subscribers
            </Typography>
            <Typography variant="h3" sx={{ mt: 1, color: 'success.main' }}>
              {stats.activeSubscribers || 0}
            </Typography>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MainCard>
            <Typography variant="body2" color="text.secondary">
              Unsubscribed
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              {stats.unsubscribedSubscribers || 0}
            </Typography>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MainCard>
            <Typography variant="body2" color="text.secondary">
              Sources
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              {(stats.sourceBreakdown || []).slice(0, 4).map((item) => (
                <Chip key={item.source} size="small" variant="outlined" label={`${item.source}: ${item.total}`} />
              ))}
              {!stats.sourceBreakdown?.length ? <Chip size="small" label="No data" /> : null}
            </Stack>
          </MainCard>
        </Grid>
      </Grid>

      <MainCard>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Search email, name, phone or tag"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="subscribed">Subscribed</MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Source"
            value={sourceFilter}
            onChange={(event) => {
              setSourceFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            {sourceOptions.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </MainCard>

      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}
      {error ? <Alert severity="error">Failed to load newsletter subscribers.</Alert> : null}
      {statsError ? <Alert severity="error">Failed to load newsletter statistics.</Alert> : null}
      {detailsError ? <Alert severity="error">Failed to load subscriber details.</Alert> : null}

      <MainCard
        title={`Subscribers (${pagination.total || subscribers.length || 0})`}
        secondary={
          <Chip
            size="small"
            label={`Page ${pagination.page || page + 1} / ${pagination.totalPages || 1}`}
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Subscribed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>Loading subscribers...</TableCell>
                </TableRow>
              ) : subscribers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No subscribers found.
                  </TableCell>
                </TableRow>
              ) : (
                subscribers.map((subscriber) => (
                  <TableRow key={subscriber._id} hover>
                    <TableCell>{subscriber.email || '-'}</TableCell>
                    <TableCell>{subscriber.name || '-'}</TableCell>
                    <TableCell>{subscriber.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusChipColor(subscriber.status)}
                        label={subscriber.status || 'unknown'}
                      />
                    </TableCell>
                    <TableCell>{subscriber.source || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {(subscriber.tags || []).slice(0, 2).map((tag) => (
                          <Chip key={tag} size="small" variant="outlined" label={tag} />
                        ))}
                        {(subscriber.tags || []).length > 2 ? (
                          <Chip size="small" variant="outlined" label={`+${subscriber.tags.length - 2}`} />
                        ) : null}
                        {!subscriber.tags?.length ? '-' : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(subscriber.subscribedAt)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                        <Tooltip title="View details">
                          <IconButton size="small" color="primary" onClick={() => openDetailsDialog(subscriber._id)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit subscriber">
                          <IconButton size="small" color="secondary" onClick={() => openEditDialog(subscriber)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete subscriber">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={submitting}
                              onClick={() => handleDelete(subscriber)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(pagination.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>

      <SubscriberFormDialog
        open={formOpen}
        mode={formMode}
        formData={formData}
        submitting={submitting}
        onClose={() => {
          if (!submitting) setFormOpen(false);
        }}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
      />

      <SubscriberDetailsDialog
        open={detailsOpen}
        data={detailsData}
        loading={detailsLoading}
        onClose={() => setDetailsOpen(false)}
      />
    </Stack>
  );
}
